import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ArrowRight, Calendar, Clock, Search, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SectionHeading } from "@/components/section-heading";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import mag1 from "@/assets/magazin-1-1024.webp";
import mag2 from "@/assets/magazin-2-1024.webp";
import mag3 from "@/assets/magazin-3-1024.webp";
import mag4 from "@/assets/magazin-4-1024.webp";
import { useLanguage } from "@/lib/i18n";
import { articleSummaries } from "@/data/article-summaries";

export const Route = createFileRoute("/artikel")({
  staticData: { sitemap: true },
  validateSearch: (search: Record<string, unknown>): { q?: string; category?: string } => ({
    q: typeof search.q === "string" ? search.q : undefined,
    category: typeof search.category === "string" ? search.category : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Magazin & Ratgeber. Begner" },
      {
        name: "description",
        content:
          "Tipps, Checklisten und Ratgeber rund um Bauen, Renovieren und Wohnen in der Schweiz.",
      },
      { property: "og:title", content: "Magazin & Ratgeber. Begner" },
      {
        property: "og:description",
        content: "Tipps und Ratgeber rund um Bauen und Renovieren.",
      },
    ],
  }),
  component: ArtikelPage,
});

type Article = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  readingTime: string;
  date: string;
  image: string;
};

const articleImages = [mag1, mag2, mag3, mag4, mag1, mag2, mag3, mag4, mag1] as const;

const articleCopy = {
  de: {
    eyebrow: "Magazin",
    title: "Wissen rund ums Bauen",
    intro:
      "Aktuelle Ratgeber, Preis-Insights und Checklisten, verfasst von Schweizer Bauexpertinnen und Baufachleuten.",
    searchPlaceholder: "Artikel durchsuchen…",
    allCategory: "Alle",
    categories: [
      { id: "boden", label: "Bodenbeläge" },
      { id: "fassade", label: "Fassade" },
      { id: "sanitaer", label: "Sanitär" },
      { id: "haustechnik", label: "Haustechnik" },
      { id: "ratgeber", label: "Ratgeber" },
      { id: "neubau", label: "Neubau" },
      { id: "reinigung", label: "Reinigung" },
      { id: "umzug", label: "Umzug" },
    ],
    featuredLabel: "Featured",
    readArticle: "Artikel lesen",
    latestTitle: "Aktuelle Beiträge",
    articleCount: "Artikel",
    emptyTitle: "Keine Artikel gefunden.",
    emptyText: "Versuchen Sie einen anderen Suchbegriff oder eine andere Kategorie.",
    ctaTitle: "Konkrete Frage zu Ihrem Projekt?",
    ctaText:
      "Stellen Sie kostenlos Ihre Anfrage, geprüfte Auftragnehmer aus Ihrer Region antworten mit individuellen Offerten.",
    ctaButton: "Anfrage eingeben",
    articles: [
      {
        id: "boden",
        title: "Parkett oder Vinyl? Der grosse Bodenvergleich 2026",
        excerpt:
          "Pflege, Preis, Wohnklima: Welcher Bodenbelag passt zu welchem Raum, und was kostet er pro m² in der Schweiz?",
        category: "Bodenbeläge",
        readingTime: "6 Min.",
        date: "12. Juni 2026",
      },
      {
        id: "fassade",
        title: "Fassadensanierung: Kosten, Förderungen und Zeitplan",
        excerpt:
          "So planen Sie Ihre Fassadensanierung Schritt für Schritt, mit Schweizer Förderprogrammen und Energieeinsparung.",
        category: "Fassade",
        readingTime: "8 Min.",
        date: "04. Juni 2026",
      },
      {
        id: "sanitaer",
        title: "Badumbau in 6 Wochen: Realistischer Bauablauf",
        excerpt:
          "Vom Rückbau bis zur Endreinigung, ein typischer Zeitplan, Stolperfallen und Tipps zur Kommunikation mit dem Handwerker.",
        category: "Sanitär",
        readingTime: "5 Min.",
        date: "28. Mai 2026",
      },
      {
        id: "haustechnik",
        title: "Wärmepumpe statt Ölheizung: Lohnt sich der Wechsel?",
        excerpt:
          "Investitionskosten, laufende Kosten und CO₂-Einsparung im Vergleich, inkl. Beispielrechnung für ein Einfamilienhaus.",
        category: "Haustechnik",
        readingTime: "7 Min.",
        date: "19. Mai 2026",
      },
      {
        id: "ratgeber",
        title: "Kostenfalle Umbau: Die 7 häufigsten Fehler",
        excerpt:
          "Vom unterschätzten Rückbau bis zur falschen Offertenstruktur, worauf Bauherren in der Schweiz besonders achten sollten.",
        category: "Ratgeber",
        readingTime: "9 Min.",
        date: "10. Mai 2026",
      },
      {
        id: "neubau",
        title: "Schweizer Holzbau: Trends und Bauzeit im Überblick",
        excerpt:
          "Warum sich modulare Holzbauten lohnen, welche Hersteller relevant sind, und wie lange ein Holzhaus heute wirklich dauert.",
        category: "Neubau",
        readingTime: "6 Min.",
        date: "02. Mai 2026",
      },
      {
        id: "reinigung",
        title: "Umzugsreinigung mit Abnahmegarantie: worauf Sie achten sollten",
        excerpt:
          "Checkliste für Offerten, Schlüsselübergabe und typische Zusatzkosten bei der Endreinigung in der Schweiz.",
        category: "Reinigung",
        readingTime: "5 Min.",
        date: "24. April 2026",
      },
      {
        id: "umzug",
        title: "Umzug planen: Kosten, Timing und die richtige Zügelfirma",
        excerpt:
          "So vergleichen Sie Umzugsofferten fair und vermeiden Lücken bei Verpackung, Transport und Möbelmontage.",
        category: "Umzug",
        readingTime: "6 Min.",
        date: "18. April 2026",
      },
      {
        id: "haustechnik",
        title: "Elektriker beauftragen: wann ein Profi zwingend nötig ist",
        excerpt:
          "Von Steckdosen bis Wallbox: welche Elektroarbeiten Sie planen lassen sollten und welche Nachweise wichtig sind.",
        category: "Haustechnik",
        readingTime: "4 Min.",
        date: "11. April 2026",
      },
    ],
  },
  fr: {
    eyebrow: "Magazine",
    title: "Tout savoir sur la construction",
    intro:
      "Guides actuels, repères de prix et check-lists rédigés par des expertes et experts suisses du bâtiment.",
    searchPlaceholder: "Rechercher des articles…",
    allCategory: "Tous",
    categories: [
      { id: "boden", label: "Revêtements de sol" },
      { id: "fassade", label: "Façade" },
      { id: "sanitaer", label: "Sanitaire" },
      { id: "haustechnik", label: "Technique du bâtiment" },
      { id: "ratgeber", label: "Guides" },
      { id: "neubau", label: "Construction neuve" },
      { id: "reinigung", label: "Nettoyage" },
      { id: "umzug", label: "Déménagement" },
    ],
    featuredLabel: "À la une",
    readArticle: "Lire l'article",
    latestTitle: "Articles récents",
    articleCount: "articles",
    emptyTitle: "Aucun article trouvé.",
    emptyText: "Essayez un autre terme de recherche ou une autre catégorie.",
    ctaTitle: "Une question concrète sur votre projet?",
    ctaText:
      "Déposez gratuitement votre demande, des prestataires vérifiés de votre région répondent avec des offres individuelles.",
    ctaButton: "Publier un mandat",
    articles: [
      {
        id: "boden",
        title: "Parquet ou vinyle? Le grand comparatif des sols 2026",
        excerpt:
          "Entretien, prix, climat intérieur: quel revêtement convient à quelle pièce et combien coûte-t-il au m² en Suisse?",
        category: "Revêtements de sol",
        readingTime: "6 min",
        date: "12 juin 2026",
      },
      {
        id: "fassade",
        title: "Rénovation de façade: coûts, subventions et calendrier",
        excerpt:
          "Planifiez votre rénovation étape par étape avec les programmes de soutien suisses et les économies d'énergie.",
        category: "Façade",
        readingTime: "8 min",
        date: "04 juin 2026",
      },
      {
        id: "sanitaer",
        title: "Rénovation de salle de bain en 6 semaines: déroulement réaliste",
        excerpt:
          "Du démontage au nettoyage final, un calendrier type, les pièges et les conseils pour communiquer avec l'artisan.",
        category: "Sanitaire",
        readingTime: "5 min",
        date: "28 mai 2026",
      },
      {
        id: "haustechnik",
        title: "Pompe à chaleur au lieu du mazout: le changement vaut-il la peine?",
        excerpt:
          "Coûts d'investissement, frais courants et réduction du CO₂ en comparaison, avec exemple pour une maison individuelle.",
        category: "Technique du bâtiment",
        readingTime: "7 min",
        date: "19 mai 2026",
      },
      {
        id: "ratgeber",
        title: "Pièges financiers en rénovation: les 7 erreurs fréquentes",
        excerpt:
          "Démolition sous-estimée, structure d'offres imprécise: ce à quoi les maîtres d'ouvrage suisses doivent faire attention.",
        category: "Guides",
        readingTime: "9 min",
        date: "10 mai 2026",
      },
      {
        id: "neubau",
        title: "Construction bois en Suisse: tendances et délais",
        excerpt:
          "Pourquoi les constructions modulaires en bois valent la peine, quels acteurs comptent et combien de temps dure vraiment un chantier.",
        category: "Construction neuve",
        readingTime: "6 min",
        date: "02 mai 2026",
      },
      {
        id: "reinigung",
        title: "Nettoyage de déménagement avec garantie: points à vérifier",
        excerpt:
          "Check-list pour les offres, la remise des clés et les coûts supplémentaires typiques du nettoyage final.",
        category: "Nettoyage",
        readingTime: "5 min",
        date: "24 avril 2026",
      },
      {
        id: "umzug",
        title: "Planifier un déménagement: coûts, délais et bonne entreprise",
        excerpt:
          "Comparez les offres de déménagement et évitez les lacunes pour l'emballage, le transport et le montage.",
        category: "Déménagement",
        readingTime: "6 min",
        date: "18 avril 2026",
      },
      {
        id: "haustechnik",
        title: "Mandater un électricien: quand un pro est indispensable",
        excerpt:
          "Prises, borne de recharge, attestations: les travaux électriques à planifier avec un spécialiste.",
        category: "Technique du bâtiment",
        readingTime: "4 min",
        date: "11 avril 2026",
      },
    ],
  },
  it: {
    eyebrow: "Rivista",
    title: "Conoscenze su costruzione e ristrutturazione",
    intro:
      "Guide aggiornate, indicazioni sui prezzi e checklist redatte da esperte ed esperti svizzeri dell'edilizia.",
    searchPlaceholder: "Cerca articoli…",
    allCategory: "Tutti",
    categories: [
      { id: "boden", label: "Pavimenti" },
      { id: "fassade", label: "Facciata" },
      { id: "sanitaer", label: "Sanitari" },
      { id: "haustechnik", label: "Tecnica domestica" },
      { id: "ratgeber", label: "Guide" },
      { id: "neubau", label: "Nuova costruzione" },
      { id: "reinigung", label: "Pulizia" },
      { id: "umzug", label: "Trasloco" },
    ],
    featuredLabel: "In evidenza",
    readArticle: "Leggi articolo",
    latestTitle: "Articoli recenti",
    articleCount: "articoli",
    emptyTitle: "Nessun articolo trovato.",
    emptyText: "Provi un altro termine di ricerca o un'altra categoria.",
    ctaTitle: "Una domanda concreta sul suo progetto?",
    ctaText:
      "Invii gratuitamente la sua richiesta; imprese verificate della sua regione rispondono con offerte personalizzate.",
    ctaButton: "Pubblica un incarico",
    articles: [
      {
        id: "boden",
        title: "Parquet o vinile? Il grande confronto dei pavimenti 2026",
        excerpt:
          "Cura, prezzo, clima abitativo: quale pavimento è adatto a quale stanza e quanto costa al m² in Svizzera?",
        category: "Pavimenti",
        readingTime: "6 min",
        date: "12 giugno 2026",
      },
      {
        id: "fassade",
        title: "Risanamento facciata: costi, incentivi e tempistiche",
        excerpt:
          "Pianifichi il risanamento passo dopo passo, con programmi svizzeri di sostegno e risparmio energetico.",
        category: "Facciata",
        readingTime: "8 min",
        date: "04 giugno 2026",
      },
      {
        id: "sanitaer",
        title: "Ristrutturare il bagno in 6 settimane: svolgimento realistico",
        excerpt:
          "Dallo smontaggio alla pulizia finale, un calendario tipico, ostacoli e consigli per comunicare con l'artigiano.",
        category: "Sanitari",
        readingTime: "5 min",
        date: "28 maggio 2026",
      },
      {
        id: "haustechnik",
        title: "Pompa di calore al posto del riscaldamento a olio: conviene?",
        excerpt:
          "Costi d'investimento, costi correnti e riduzione di CO₂ a confronto, con esempio per una casa unifamiliare.",
        category: "Tecnica domestica",
        readingTime: "7 min",
        date: "19 maggio 2026",
      },
      {
        id: "ratgeber",
        title: "Trappole nei costi di ristrutturazione: i 7 errori più comuni",
        excerpt:
          "Dallo smontaggio sottovalutato alla struttura sbagliata delle offerte: a cosa devono prestare attenzione i committenti.",
        category: "Guide",
        readingTime: "9 min",
        date: "10 maggio 2026",
      },
      {
        id: "neubau",
        title: "Costruzione in legno svizzera: tendenze e tempi",
        excerpt:
          "Perché convengono gli edifici modulari in legno, quali produttori contano e quanto dura davvero la costruzione.",
        category: "Nuova costruzione",
        readingTime: "6 min",
        date: "02 maggio 2026",
      },
      {
        id: "reinigung",
        title: "Pulizia trasloco con garanzia: a cosa fare attenzione",
        excerpt:
          "Checklist per offerte, consegna chiavi e costi aggiuntivi tipici della pulizia finale in Svizzera.",
        category: "Pulizia",
        readingTime: "5 min",
        date: "24 aprile 2026",
      },
      {
        id: "umzug",
        title: "Pianificare un trasloco: costi, tempi e impresa giusta",
        excerpt:
          "Confronti le offerte di trasloco ed eviti lacune per imballaggio, trasporto e montaggio mobili.",
        category: "Trasloco",
        readingTime: "6 min",
        date: "18 aprile 2026",
      },
      {
        id: "haustechnik",
        title: "Incaricare un elettricista: quando serve un professionista",
        excerpt:
          "Prese, wallbox e certificati: quali lavori elettrici vanno pianificati con uno specialista.",
        category: "Tecnica domestica",
        readingTime: "4 min",
        date: "11 aprile 2026",
      },
    ],
  },
  en: {
    eyebrow: "Magazine",
    title: "Expertise for building and renovation",
    intro: "Current guides, price insights and checklists written by Swiss construction experts.",
    searchPlaceholder: "Search articles…",
    allCategory: "All",
    categories: [
      { id: "boden", label: "Flooring" },
      { id: "fassade", label: "Facade" },
      { id: "sanitaer", label: "Plumbing" },
      { id: "haustechnik", label: "Building services" },
      { id: "ratgeber", label: "Guides" },
      { id: "neubau", label: "New builds" },
      { id: "reinigung", label: "Cleaning" },
      { id: "umzug", label: "Moving" },
    ],
    featuredLabel: "Featured",
    readArticle: "Read article",
    latestTitle: "Latest articles",
    articleCount: "articles",
    emptyTitle: "No articles found.",
    emptyText: "Try another search term or category.",
    ctaTitle: "A specific question about your project?",
    ctaText:
      "Submit your request free of charge and receive tailored quotes from verified contractors in your region.",
    ctaButton: "Post a job",
    articles: [
      {
        id: "boden",
        title: "Parquet or vinyl? The complete flooring comparison for 2026",
        excerpt:
          "Care, price and indoor climate: which flooring suits each room, and what does it cost per m² in Switzerland?",
        category: "Flooring",
        readingTime: "6 min",
        date: "12 June 2026",
      },
      {
        id: "fassade",
        title: "Facade renovation: costs, grants and timeline",
        excerpt:
          "Plan your facade renovation step by step, including Swiss funding programmes and energy savings.",
        category: "Facade",
        readingTime: "8 min",
        date: "4 June 2026",
      },
      {
        id: "sanitaer",
        title: "Bathroom renovation in 6 weeks: a realistic schedule",
        excerpt:
          "From strip-out to final cleaning: a typical schedule, common pitfalls and tips for working with tradespeople.",
        category: "Plumbing",
        readingTime: "5 min",
        date: "28 May 2026",
      },
      {
        id: "haustechnik",
        title: "Heat pump instead of oil heating: is it worth switching?",
        excerpt:
          "A comparison of investment, running costs and CO₂ savings, including an example for a detached house.",
        category: "Building services",
        readingTime: "7 min",
        date: "19 May 2026",
      },
      {
        id: "ratgeber",
        title: "Renovation cost traps: the 7 most common mistakes",
        excerpt:
          "From underestimated strip-out work to poorly structured quotes: what Swiss property owners should watch for.",
        category: "Guides",
        readingTime: "9 min",
        date: "10 May 2026",
      },
      {
        id: "neubau",
        title: "Swiss timber construction: trends and build times",
        excerpt:
          "Why modular timber buildings pay off, which providers matter and how long a timber home really takes today.",
        category: "New builds",
        readingTime: "6 min",
        date: "2 May 2026",
      },
      {
        id: "reinigung",
        title: "End-of-tenancy cleaning with a handover guarantee: what to check",
        excerpt:
          "A checklist for quotes, key handover and typical extra costs for final cleaning in Switzerland.",
        category: "Cleaning",
        readingTime: "5 min",
        date: "24 April 2026",
      },
      {
        id: "umzug",
        title: "Planning a move: costs, timing and the right moving company",
        excerpt:
          "Compare moving quotes fairly and avoid gaps in packing, transport and furniture assembly.",
        category: "Moving",
        readingTime: "6 min",
        date: "18 April 2026",
      },
      {
        id: "haustechnik",
        title: "Hiring an electrician: when a professional is essential",
        excerpt:
          "From sockets to EV chargers: which electrical work needs professional planning and which certificates matter.",
        category: "Building services",
        readingTime: "4 min",
        date: "11 April 2026",
      },
    ],
  },
} as const;

function ArtikelPage() {
  const { language } = useLanguage();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const search = Route.useSearch();
  const copy = articleCopy[language];
  const articles: Article[] = copy.articles.map((article, index) => ({
    ...article,
    slug: articleSummaries[index].slug,
    image: articleImages[index],
  }));
  // Additional articles use their own metadata, without shifting the existing translations.
  articles.unshift(
    ...articleSummaries.slice(copy.articles.length).map((article) => ({
      id: article.categoryId,
      slug: article.slug,
      title: article.title,
      excerpt: article.excerpt,
      category: article.category,
      readingTime: article.readingTime,
      date: article.date,
      image: articleImages[article.imageIndex],
    })),
  );
  const categories = [{ id: "all", label: copy.allCategory }, ...copy.categories];
  const initialCategory = categories.some((category) => category.id === search.category)
    ? search.category
    : "all";
  const [query, setQuery] = useState(search.q ?? "");
  const [cat, setCat] = useState(initialCategory ?? "all");

  const filtered = useMemo(
    () =>
      articles.filter((a) => {
        if (cat !== "all" && a.id !== cat) return false;
        if (query.trim()) {
          const q = query.toLowerCase();
          return a.title.toLowerCase().includes(q) || a.excerpt.toLowerCase().includes(q);
        }
        return true;
      }),
    [articles, query, cat],
  );

  const featured = articles[0];
  const showFeatured = cat === "all" && !query;
  const rest = showFeatured ? filtered.filter((a) => a.title !== featured.title) : filtered;

  if (pathname.startsWith("/artikel/")) {
    return <Outlet />;
  }

  return (
    <>
      {/* Header */}
      <section className="border-b border-border bg-surface">
        <div className="container-page py-12 md:py-16">
          <SectionHeading
            eyebrow={copy.eyebrow}
            title={copy.title}
            description={copy.intro}
            level="page"
            className="max-w-2xl"
          />

          <div className="mt-8 flex flex-col gap-4 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={copy.searchPlaceholder}
                className="h-12 rounded-xl border-border bg-card pl-12"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCat(c.id)}
                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                    cat === c.id
                      ? "border-brand bg-brand text-primary-foreground shadow-brand"
                      : "border-border bg-card hover:border-brand"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured */}
      {showFeatured && (
        <section className="bg-background py-12">
          <div className="container-page">
            <article className="grid overflow-hidden rounded-3xl border border-border bg-card shadow-card md:grid-cols-2">
              <div className="relative aspect-[4/3] md:aspect-auto">
                <img
                  src={featured.image}
                  alt={featured.title}
                  width={1024}
                  height={704}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex flex-col justify-center p-8 md:p-12">
                <Badge className="w-fit bg-brand-soft text-brand-deep hover:bg-brand-soft">
                  {copy.featuredLabel} · {featured.category}
                </Badge>
                <h2 className="mt-4 font-display text-3xl font-bold tracking-tight md:text-4xl">
                  {featured.title}
                </h2>
                <p className="mt-4 text-muted-foreground">{featured.excerpt}</p>
                <div className="mt-6 flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-4 w-4" /> {featured.date}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-4 w-4" /> {featured.readingTime}
                  </span>
                </div>
                <Button asChild className="mt-6 w-fit font-semibold">
                  <Link to="/artikel/$slug" params={{ slug: featured.slug }}>
                    {copy.readArticle}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </article>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="bg-surface py-16">
        <div className="container-page">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl font-bold md:text-3xl">{copy.latestTitle}</h2>
            <p className="text-sm text-muted-foreground">
              {filtered.length} {copy.articleCount}
            </p>
          </div>

          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {rest.map((a) => (
              <Link
                key={a.slug}
                to="/artikel/$slug"
                params={{ slug: a.slug }}
                className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-card transition hover:-translate-y-1 hover:border-brand hover:shadow-brand"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={a.image}
                    alt={a.title}
                    width={1024}
                    height={704}
                    loading="lazy"
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-deep">
                    <Tag className="h-3.5 w-3.5" />
                    {a.category}
                  </div>
                  <h3 className="mt-3 font-display text-xl font-bold leading-snug text-foreground">
                    {a.title}
                  </h3>
                  <p className="mt-2 flex-1 text-sm text-muted-foreground">{a.excerpt}</p>
                  <div className="mt-5 flex items-center justify-between border-t border-border pt-4 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> {a.date}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" /> {a.readingTime}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="mt-8 rounded-2xl border border-dashed border-border bg-card p-12 text-center">
              <p className="font-semibold text-foreground">{copy.emptyTitle}</p>
              <p className="mt-2 text-sm text-muted-foreground">{copy.emptyText}</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-gradient-hero py-16">
        <div className="container-page relative grid gap-6 md:grid-cols-[1.4fr_1fr] md:items-center">
          <div>
            <h2 className="font-display text-3xl font-bold text-white md:text-4xl">
              {copy.ctaTitle}
            </h2>
            <p className="mt-3 max-w-xl text-white/85">{copy.ctaText}</p>
          </div>
          <div className="md:text-right">
            <Button asChild size="lg" variant="secondary" className="font-semibold">
              <Link to="/auftrag-einstellen">
                {copy.ctaButton}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
